import { DateTime } from 'luxon';

import { request } from './request';
import { getMerchantId, getUserId } from './user.service';
import { parseObjectToQueryParams } from 'src/helpers/functional-helpers';
import { HandleTechnicalError } from 'src/context/technical-error-context';
import { TerminalTransaction } from 'src/common/types';
import { Api } from 'src/common';
import { authenticate } from './auth-service';

type Sort = { empty: boolean; unsorted: boolean; sorted: boolean };

type Pageable = {
  offset: number;
  pageNumber: number;
  pageSize: number;
  paged: boolean;
  sort: Sort;
  unpaged: boolean;
};

type GetSettlementsResult = {};

type AccountTransactionsResult = {
  Rows: TerminalTransaction[];
  NumberOfPages: number;
};

interface IAccountTransactionsService {
  getSettlements(
    localTransactionFromDate: Date,
    localTransactionToDate: Date,
    abortController: AbortController,
    pageNumber: number,
    handleErrorResponse: HandleTechnicalError,
    showToastOnError: boolean,
    suppressTealiumEvent: boolean,
  ): Promise<GetSettlementsResult>;
  getSettlementsV2(
    fromDate: Date,
    toDate: Date,
    abortController: AbortController,
    pageNumber: number,
  ): Promise<GetSettlementsResult>;
  getAccountTransactions(
    settlementUUID: string | null,
    localTransactionFromDate: Date | null,
    localTransactionToDate: Date | null,
    abortController?: AbortController,
    pageNumber?: number,
    reference?: string | null,
    fromAmount?: string | null,
    toAmount?: string | null,
    transactionType?: string | null,
    handleErrorResponse?: HandleTechnicalError,
    showToastOnError?: boolean,
    suppressTealiumEvent?: boolean,
  ): Promise<AccountTransactionsResult>;
  getAccountTransactionsV2(
    settleMentUUID: string | null,
    dateFrom: Date,
    dateTo: Date,
    pageSize: number,
    pageNumber?: number,
    abortController?: AbortController,
  ): Promise<AccountTransactionsResult>;
}

type AccountTransactionsResponse = {
  content: TerminalTransaction[];
  empty: boolean;
  first: boolean;
  last: boolean;
  number: number;
  numberOfElements: number;
  pageable: Pageable;
  size: number;
  sort: Sort;
};

class AccountTransactionsService implements IAccountTransactionsService {
  private ACCOUNT_TRANSACTIONS_ENDPOINT = 'account-transactions';

  async getSettlements(
    localTransactionFromDate: Date,
    localTransactionToDate: Date,
    abortController: AbortController,
    pageNumber: number,
    handleErrorResponse?: HandleTechnicalError,
    showToastOnError: boolean = true,
    suppressTealiumEvent: boolean = false,
  ) {
    const userId = getUserId();
    const merchantId = getMerchantId();

    const queryParams = {
      userReference: userId,
      merchantID: merchantId,
      processingFromDate:
        DateTime.fromJSDate(localTransactionFromDate).toFormat('yyyy-MM-dd HH:mm:ss'),
      processingToDate: DateTime.fromJSDate(localTransactionToDate).toFormat('yyyy-MM-dd HH:mm:ss'),
      channelId: 'WEB',
      channelName: 'Vodacom',
      settlementConfirmations: 99,
      pageNumber: pageNumber,
    };

    const res = await request(
      `${this.ACCOUNT_TRANSACTIONS_ENDPOINT}?${parseObjectToQueryParams(queryParams)}`,
      {
        fetchOptions: {
          method: 'GET',
          signal: abortController.signal,
        },
      },
      null,
      null,
      showToastOnError,
      suppressTealiumEvent,
    );

    const isAborted = !!abortController && abortController.signal.aborted;
    if (res?.status === 200) {
      return res.result || [];
    } else if (handleErrorResponse && !isAborted) {
      handleErrorResponse(
        'error response from the account-transactions GET endpoint (settlements)',
        true,
        suppressTealiumEvent,
      );
    }
  }

  async getSettlementsV2(
    fromDate: Date,
    toDate: Date,
    abortController: AbortController,
    pageNumber: number,
  ): Promise<GetSettlementsResult> {
    const accessToken = await authenticate();

    if (!accessToken) {
      throw new Error('Account transactions: unable to authenticate');
    }

    const merchantId = getMerchantId();

    const params = {
      accountType: 'MERCHANT',
      merchantID: merchantId,
      dateFrom: DateTime.fromJSDate(fromDate).toFormat('yyyy-MM-dd'),
      dateTo: DateTime.fromJSDate(toDate).toFormat('yyyy-MM-dd'),
      pageSize: 10,
      pageNumber: pageNumber,
    };

    const queryString = parseObjectToQueryParams(params);
    const queryUrl = `${this.ACCOUNT_TRANSACTIONS_ENDPOINT}?${queryString}`;

    try {
      const { status, response } = await Api.getPrivateApiV2<AccountTransactionsResponse>(
        queryUrl,
        {
          signal: abortController?.signal,
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      // const isAborted = !!abortController && abortController.signal.aborted;
      if (status === 200) {
        return { Rows: response.content, NumberOfPages: response?.number };
      } else {
        throw Error(
          `Account transactions: error response ${queryUrl} GET endpoint: response code: ${status}, response: ${JSON.stringify(
            response,
          )}`,
        );
      }
    } catch (error) {
      console.error(error);
      throw Error(
        "Uh oh! We're experiencing technical difficulties, but we're working on it. Please try again later.",
      );
    }
  }

  async getAccountTransactions(
    settlementUUID: string | null,
    localTransactionFromDate: Date | null,
    localTransactionToDate: Date | null,
    abortController?: AbortController,
    pageNumber?: number,
    reference?: string | null,
    fromAmount?: string | null,
    toAmount?: string | null,
    transactionType?: string | null,
    handleErrorResponse?: HandleTechnicalError,
    showToastOnError: boolean = true,
    suppressTealiumEvent: boolean = false,
  ) {
    const userId = getUserId();
    const merchantId = getMerchantId();
    let isSearchOrFilter = false;

    if (
      !!fromAmount ||
      !!toAmount ||
      !!reference ||
      (!!transactionType && !transactionType.startsWith('All'))
    ) {
      isSearchOrFilter = true;
    }

    let queryParams: GetTransactionsParams = {
      userReference: userId,
      merchantID: merchantId,
      channelId: 'WEB',
      channelName: 'Vodacom',
      transactionFromAmount: fromAmount,
      transactionToAmount: toAmount,
      retrievalReferenceNumber: reference,
      transactionTypeCode: transactionType,
      pageNumber: pageNumber,
      settlementConfirmationUUID: undefined,
      processingFromDate: undefined,
      processingToDate: undefined,
    };

    if (!!settlementUUID) {
      queryParams = {
        ...queryParams,
        settlementConfirmationUUID: !isSearchOrFilter ? settlementUUID : undefined,
      };
    } else {
      queryParams = {
        ...queryParams,
        processingFromDate: localTransactionFromDate
          ? DateTime.fromJSDate(localTransactionFromDate).toFormat('yyyy-MM-dd')
          : undefined,
        processingToDate: localTransactionToDate
          ? DateTime.fromJSDate(localTransactionToDate).toFormat('yyyy-MM-dd')
          : undefined,
      };
    }

    const res = await request(
      `${this.ACCOUNT_TRANSACTIONS_ENDPOINT}?${parseObjectToQueryParams(queryParams)}`,
      {
        fetchOptions: {
          method: 'GET',
          signal: abortController?.signal,
        },
      },
      null,
      null,
      showToastOnError,
      suppressTealiumEvent,
    );

    const isAborted = !!abortController && abortController.signal.aborted;
    if (res?.status === 200) {
      return res.result || [];
    } else if (handleErrorResponse && !isAborted) {
      handleErrorResponse(
        'error response from the account-transactions GET endpoint (transactions)',
        true,
        suppressTealiumEvent,
      );
    }
  }

  async getAccountTransactionsV2(
    settleMentUUID: string | null,
    dateFrom: Date | null,
    dateTo: Date | null,
    pageSize: number = 10,
    pageNumber?: number,
    abortController?: AbortController,
  ): Promise<AccountTransactionsResult> {
    const accessToken = await authenticate();

    if (!accessToken) {
      throw new Error('Account transactions: unable to authenticate');
    }

    const merchantId = getMerchantId();

    const params = {
      accountType: 'MERCHANT',
      merchantID: merchantId,
      dateFrom: dateFrom ? DateTime.fromJSDate(dateFrom).toFormat('yyyy-MM-dd') : undefined,
      dateTo: dateTo ? DateTime.fromJSDate(dateTo).toFormat('yyyy-MM-dd') : undefined,
      pageSize: pageSize,
      pageNumber: pageNumber,
      transactionUUID: settleMentUUID,
    };

    const queryString = parseObjectToQueryParams(params);
    const queryUrl = `${this.ACCOUNT_TRANSACTIONS_ENDPOINT}?${queryString}`;

    try {
      const { status, response } = await Api.getPrivateApiV2<AccountTransactionsResponse>(
        queryUrl,
        {
          signal: abortController?.signal,
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      // const isAborted = !!abortController && abortController.signal.aborted;
      if (status === 200) {
        return { Rows: response.content, NumberOfPages: response?.number };
      } else {
        throw Error(
          `Account transactions: error response ${queryUrl} GET endpoint: response code: ${status}, response: ${JSON.stringify(
            response,
          )}`,
        );
      }
    } catch (error) {
      console.error(error);
      throw Error(
        "Uh oh! We're experiencing technical difficulties, but we're working on it. Please try again later.",
      );
    }
  }
}

type GetTransactionsParams = {
  userReference: string | null;
  merchantID: string;
  channelId: 'WEB';
  channelName: 'Vodacom';
  transactionFromAmount?: string | null;
  transactionToAmount?: string | null;
  retrievalReferenceNumber?: string | null;
  transactionTypeCode?: string | null;
  pageNumber?: number;
  settlementConfirmationUUID?: string;
  processingFromDate?: string;
  processingToDate?: string;
};

const accountTransactionsService = new AccountTransactionsService();

export { accountTransactionsService };
